/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, DestroyRef, Inject, OnInit} from '@angular/core';
import {UIRouterModule} from '@uirouter/angular';
import template from './payment-list-page.component.html';
import {SignalAdminListColumn, SignalAdminResourceListState, SignalAdminResourceListStateFactory} from '../common/controller/signal-admin-resource-list-state';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {BrowserLocationService, WINDOW} from '../angular-platform-providers';
import {PaymentApiService, PaymentModel} from './payment-api.service';
import {PaymentExportService} from './payment-export.service';
import {AppSharedModule} from '../common/app-shared.module';
import {SignalAdminResourceListComponent} from '../common/controller/signal-admin-resource-list.component';
import {PaymentStatusLabelComponent} from './payment-status-component';

@Component({
  standalone: true,
  imports: [AppSharedModule, UIRouterModule, SignalAdminResourceListComponent, PaymentStatusLabelComponent],
  selector: 'adn-payment-list-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentListPageComponent implements OnInit {
  readonly list: SignalAdminResourceListState<PaymentModel>;
  readonly columns: SignalAdminListColumn[] = [
    {param: 'createTime'},
    {param: 'status'},
    {param: 'amount', label: 'budgetAmount'},
    {param: 'billedAmount'},
    {param: 'refundedAmount'},
    {label: 'coupon', param: '', sortable: false},
    {label: 'lineItem', param: '', sortable: false}
  ];

  constructor(
    api: PaymentApiService,
    factory: SignalAdminResourceListStateFactory,
    location: BrowserLocationService,
    destroyRef: DestroyRef,
    @Inject(LocalUserProfile) localUserProfile: LocalUserProfile,
    @Inject(WINDOW) window: Window,
    readonly paymentExport: PaymentExportService
  ) {
    this.list = factory.create(api, {
      listId: 'fullPaymentList', defaultOrderBy: 'createTime', defaultAscending: false,
      saveSortSettings: (settings) => {
        location.setQueryParam('pageNo', undefined);
        const profile = localUserProfile.getProfile() || {};
        localUserProfile.saveProfile({...profile, adnLists: {...profile.adnLists, fullPaymentList: {...profile.adnLists?.fullPaymentList, ...settings}}});
      }
    }, destroyRef);
    this.list.readOnly.set(true);
    const onPopState = () => {
      const value = Number(new URL(window.location.href).searchParams.get('pageNo'));
      const page = Number.isFinite(value) && value > 0 ? value : 1;
      if (page !== this.list.currentPage()) {
        this.list.currentPage.set(page);
        this.list.load();
      }
    };
    window.addEventListener('popstate', onPopState);
    destroyRef.onDestroy(() => window.removeEventListener('popstate', onPopState));
  }

  ngOnInit(): void { this.list.load(); }
}
